/* eslint-disable rxjs-angular-x/prefer-composition */
import {
    HttpEvent,
    HttpEventType,
    HttpHandler,
    HttpInterceptor,
    HttpRequest,
    HttpResponse,
} from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { Observable, tap } from "rxjs";

import { Context, INFRA_SETTINGS, RESPONSE } from "@hermes/core-app-core";
import { InfraSettings } from "@hermes/core-env-infra";

/**
 * This interceptor catch any response cache-tag header and aggregate it into reply.
 */
@Injectable()
export class CacheTagInterceptor implements HttpInterceptor {
    private readonly context = inject(Context);
    private readonly response = inject(RESPONSE);
    private readonly settings = inject<InfraSettings>(INFRA_SETTINGS);

    /**
     * Angular HTTP interceptor
     */
    public intercept<T>(
        request: HttpRequest<T>,
        next: HttpHandler,
    ): Observable<HttpEvent<T>> {
        // If the current interceptor is called in browser mode,
        // we only call next.handle without any custom process because
        // there is no CDN catching the response.
        if (this.context.isInBrowserMode()) {
            return next.handle(request);
        }

        // Also, we only care about BCK calls.
        if (
            this.settings.backendSSR !== undefined &&
            !request.url.startsWith(this.settings.backendSSR)
        ) {
            return next.handle(request);
        }

        return next.handle(request).pipe(
            tap((event) => {
                if (
                    event instanceof HttpResponse &&
                    event.type === HttpEventType.Response &&
                    event.headers.has("cache-tag")
                ) {
                    const responseCacheTags = event.headers.get("cache-tag");
                    const existingCacheTags =
                        this.response.getHeader("cache-tag");

                    const uniqueCacheTags = this.formatCacheTags(
                        existingCacheTags,
                        responseCacheTags,
                    );

                    if (uniqueCacheTags.length > 0) {
                        this.response.header(
                            "cache-tag",
                            uniqueCacheTags.join(","),
                        );
                    }
                }
            }),
        );
    }

    private formatCacheTags(
        existingCacheTags: number | string | string[] | undefined,
        responseCacheTags: string | null,
    ) {
        const mergedCacheTags = [
            ...this.normalizeTags(existingCacheTags),
            ...this.normalizeTags(responseCacheTags),
        ];

        return [...new Set(mergedCacheTags)];
    }

    private normalizeTags(
        value: string | string[] | number | undefined | null,
    ): string[] {
        if (!value) {
            return [];
        }

        // Node response header may return string | string[]
        const normalizedValue = Array.isArray(value)
            ? value.join(",")
            : String(value);

        return normalizedValue
            .split(",")
            .map((tag) => tag.trim())
            .filter(Boolean);
    }
}
